import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import React, { useState } from "react";

interface RecentFit {
  id: string;
  imageUrl?: string;
  title: string;
  subtitle: string;
}

interface PictureInPictureWindowProps {
  imageUrl: string | null;
  shopLink: string;
  onShopLinkChange: (link: string) => void;
  onFitCheck: () => void;
  analyzing: boolean;
  disabled: boolean;
  recentFits?: RecentFit[];
  onFitClick?: (fitId: string) => void;
  error?: string | null;
  onErrorClose?: () => void;
}

const PictureInPictureWindow: React.FC<PictureInPictureWindowProps> = ({
  imageUrl,
  shopLink,
  onShopLinkChange,
  onFitCheck,
  analyzing,
  disabled,
  recentFits = [],
  onFitClick,
  error = null,
  onErrorClose,
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Sync error prop with local snackbar state
  React.useEffect(() => {
    if (error) {
      setSnackbarOpen(true);
    }
  }, [error]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    if (onErrorClose) {
      onErrorClose();
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
          p: 1.5,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexShrink: 0 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
            FitCheckr
          </Typography>
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            overflow: "auto",
            minHeight: 0,
            pb: 10, // Add padding at bottom to prevent content from being hidden behind fixed controls
          }}
        >
          {/* Main Image Display */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "background.primary",
              borderRadius: 2,
              minHeight: "150px",
              overflow: "hidden",
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt="Your Photo"
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: 2,
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No image selected
                <br />
                Please upload a photo in the main window
              </Typography>
            )}
          </Box>

          {/* Recent Fits */}
          {recentFits.length > 0 && (
            <Box sx={{ flexShrink: 0 }}>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {recentFits.slice(0, 3).map((fit) => (
                  <Box key={fit.id} sx={{ flex: 1 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 0.5,
                        cursor: fit.imageUrl ? "pointer" : "default",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: fit.imageUrl ? "action.hover" : "transparent",
                          transform: fit.imageUrl ? "translateY(-1px)" : "none",
                        },
                      }}
                      onClick={() => fit.imageUrl && onFitClick?.(fit.id)}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: "60px",
                          bgcolor: "grey.200",
                          borderRadius: 1,
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {fit.imageUrl ? (
                          <Box
                            component="img"
                            src={fit.imageUrl}
                            alt={fit.title}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <CircularProgress size={16} />
                        )}
                      </Box>
                    </Paper>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Fixed Bottom Section - URL Input and Button */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            p: 1.5,
            bgcolor: "background.default",
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <TextField
            fullWidth
            label="Image Address"
            placeholder="Enter clothing item URL"
            value={shopLink}
            onChange={(e) => onShopLinkChange(e.target.value)}
            variant="outlined"
            size="small"
            disabled={analyzing || disabled}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.875rem",
              },
            }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={onFitCheck}
            disabled={analyzing || disabled || !shopLink.trim() || !imageUrl}
            size="small"
            sx={{
              py: 0.75,
              fontWeight: 600,
              textTransform: "none",
            }}
            startIcon={analyzing ? <CircularProgress size={16} /> : undefined}
          >
            {analyzing ? "Generating..." : "Fit Check!"}
          </Button>
        </Box>
      </Paper>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          variant="filled"
          sx={{
            minWidth: "300px",
            maxWidth: "400px",
          }}
        >
          {error || "An error occurred"}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PictureInPictureWindow;
